import { Fragment, useEffect, useState } from "react";
import { api } from "@/client/lib/api";
import { AdminShell } from "@/client/components/admin/AdminShell";
import { AdminPageHeader } from "@/client/components/admin/AdminPageHeader";
import { ActivityEditDialog } from "@/client/components/admin/ActivityEditDialog";
import { Button } from "@/client/components/ui/button";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableSectionRow,
} from "@/client/components/ui/table";
import {
  Dialog,
  DialogTrigger,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogPanel,
} from "@/client/components/ui/dialog";
import { PlusIcon, PencilIcon, ChevronUpIcon, ChevronDownIcon } from "lucide-react";

type Programme = {
  id: number;
  title: string;
  shortDescription: string | null;
  // Lowest eligibility leerjaar (1-8), or null when the activity has none.
  minYearLevel: number | null;
  // Enrolment mode of the activity's primary offering ('vast' | 'keuze' | 'technohub' |
  // 'maatwerk'), or null when it has no offering / an unknown mode.
  enrolmentModeCode: string | null;
  // The primary offering's id + admin-controlled display order. Both null when the programme has
  // no offering yet. Drive the per-category reorder controls (persist via PUT offerings/order).
  offeringId: number | null;
  sortOrder: number | null;
};

// The leerjaar-category each activity falls under, in display order. An activity is
// placed by the category of its LOWEST eligibility leerjaar. "Overig" trails for
// activities without a leerjaar.
const CATEGORY_ORDER = [
  "Groep 1 en 2",
  "Groep 3 en 4",
  "Groep 5",
  "Groep 6",
  "Groep 7",
  "Groep 8",
  "Overig",
] as const;

type Category = (typeof CATEGORY_ORDER)[number];

function categoryFor(minYearLevel: number | null): Category {
  switch (minYearLevel) {
    case 1:
    case 2:
      return "Groep 1 en 2";
    case 3:
    case 4:
      return "Groep 3 en 4";
    case 5:
      return "Groep 5";
    case 6:
      return "Groep 6";
    case 7:
      return "Groep 7";
    case 8:
      return "Groep 8";
    default:
      return "Overig";
  }
}

// The enrolment-mode sub-sections shown under each leerjaar-category, in display order.
// Each programme is placed by its primary offering's enrolment mode; anything unknown or
// missing falls into the trailing "Overig" sub-section.
const MODE_SECTIONS: { label: string; code: string | null }[] = [
  { label: "Vast programma", code: "vast" },
  { label: "Keuzeprogramma", code: "keuze" },
  { label: "TechnoHub", code: "technohub" },
  { label: "Deskundigheidsbevordering", code: "deskundigheidsbevordering" },
  { label: "Maatwerk", code: "maatwerk" },
  { label: "Overig", code: null },
];

const KNOWN_MODE_CODES = new Set(
  MODE_SECTIONS.map((s) => s.code).filter((c): c is string => c !== null)
);

// Match a programme to a mode sub-section. Unknown or missing modes land in "Overig" (code null).
function modeMatches(enrolmentModeCode: string | null, sectionCode: string | null): boolean {
  if (sectionCode === null) {
    return enrolmentModeCode === null || !KNOWN_MODE_CODES.has(enrolmentModeCode);
  }
  return enrolmentModeCode === sectionCode;
}

const titleCollator = new Intl.Collator("nl");

// The programme/activity catalogue. Reuses the original ProgrammesPage behaviour (list +
// create dialog + "Bewerken" link to the activity editor) inside the new admin shell.
export function AanbodPage() {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [loading, setLoading] = useState(true);

  // Create form state
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // The activity being edited in the editor dialog (string id), or null when closed.
  const [editingId, setEditingId] = useState<string | null>(null);

  // True while a reorder PUT is in flight, so the up/down controls disable to avoid overlapping
  // writes (a second reorder before reload would diff against stale positions).
  const [reordering, setReordering] = useState(false);

  async function load() {
    try {
      const res = await api.api.admin.programmes.$get();
      if (res.ok) {
        const data = await res.json();
        setProgrammes(data.programmes as Programme[]);
      }
    } catch {
      // ignore; the empty state covers a failed load
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate() {
    setCreating(true);
    setError(null);
    try {
      const res = await api.api.admin.programmes.$post({
        json: {
          title,
          ...(shortDescription && { shortDescription }),
          ...(longDescription && { longDescription }),
        },
      });
      if (res.ok) {
        setTitle("");
        setShortDescription("");
        setLongDescription("");
        setDialogOpen(false);
        load();
      } else {
        setError("Aanmaken mislukt. Controleer de invoer.");
      }
    } catch {
      setError("Er is iets misgegaan.");
    } finally {
      setCreating(false);
    }
  }

  // Group activities by leerjaar-category (primary grouping), then by enrolment mode
  // (secondary/sub grouping). Within each mode sub-section, sort by the admin-controlled
  // sort_order (lower first), falling back to title for ties / not-yet-ordered entries. This is
  // the SAME order the school portal shows (GET /eligible orders offerings by sort_order). Both
  // empty categories and empty sub-sections are omitted; the fixed category and mode order is
  // preserved.
  const groups = CATEGORY_ORDER.map((category) => {
    const inCategory = programmes.filter((p) => categoryFor(p.minYearLevel) === category);
    const subgroups = MODE_SECTIONS.map((section) => ({
      label: section.label,
      code: section.code,
      items: inCategory
        .filter((p) => modeMatches(p.enrolmentModeCode, section.code))
        .sort(
          (a, b) =>
            (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
            titleCollator.compare(a.title, b.title)
        ),
    })).filter((s) => s.items.length > 0);
    return { category, subgroups };
  }).filter((g) => g.subgroups.length > 0);

  // Move an entry one slot up/down WITHIN its sub-section and persist the new order. We send the
  // sub-section's full ordered list of offering ids to PUT /admin/offerings/order (1-based
  // positions), so the portal + this page agree. Optimistic: reorder local state first, then
  // reload on success to pick up the server-assigned sort_order. Entries without an offering
  // (offeringId null) can't be ordered and are skipped.
  async function reorderWithin(items: Programme[], index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= items.length || reordering) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    const offeringIds = next.map((p) => p.offeringId).filter((id): id is number => id != null);
    if (offeringIds.length < 2) return;

    setReordering(true);
    try {
      const res = await (api.api.admin.offerings as any).order.$put({ json: { offeringIds } });
      if (res.ok) await load();
    } catch {
      // a failed reorder leaves the server order unchanged; the next load() reflects truth
    } finally {
      setReordering(false);
    }
  }

  return (
    <AdminShell>
      <AdminPageHeader
        items={[{ label: "Aanbod" }]}
        subtitle={loading ? "Laden..." : `${programmes.length} programma${programmes.length === 1 ? "" : "'s"}`}
        action={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger
              render={
                <Button size="sm">
                  <PlusIcon className="mr-2 size-4" />
                  Nieuw programma
                </Button>
              }
            />
            <DialogPopup>
              <DialogHeader>
                <DialogTitle>Nieuw programma</DialogTitle>
                <DialogDescription>Maak een nieuw programma aan.</DialogDescription>
              </DialogHeader>
              <DialogPanel>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Titel *</Label>
                    <Input
                      id="title"
                      placeholder="Theater in de klas"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="shortDesc">Korte beschrijving</Label>
                    <Input
                      id="shortDesc"
                      placeholder="Een interactieve theatervoorstelling"
                      value={shortDescription}
                      onChange={(e) => setShortDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="longDesc">Uitgebreide beschrijving</Label>
                    <textarea
                      id="longDesc"
                      className="flex min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-xs/5 placeholder:text-muted-foreground/72 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/24"
                      placeholder="Volledige beschrijving van het programma..."
                      value={longDescription}
                      onChange={(e) => setLongDescription(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                </div>
              </DialogPanel>
              <DialogFooter>
                <DialogClose render={<Button variant="outline">Annuleren</Button>} />
                <Button onClick={handleCreate} disabled={creating || !title}>
                  {creating ? "Bezig..." : "Aanmaken"}
                </Button>
              </DialogFooter>
            </DialogPopup>
          </Dialog>
        }
      />

      {loading ? (
        <div className="space-y-2 rounded-lg border p-3">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ) : programmes.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen programma's aangemaakt.</p>
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.category}>
              <h2 className="mb-3 text-sm font-semibold text-foreground">{group.category}</h2>
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Programma</TableHead>
                    <TableHead className="w-full">Beschrijving</TableHead>
                    <TableHead className="text-center">Volgorde</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.subgroups.map((sub) => (
                    <Fragment key={sub.label}>
                      <TableSectionRow colSpan={4}>{sub.label}</TableSectionRow>
                      {sub.items.map((prog, idx) => (
                        <TableRow key={prog.id}>
                          <TableCell className="whitespace-nowrap font-medium text-foreground">
                            {prog.title}
                          </TableCell>
                          <TableCell className="w-full text-muted-foreground">
                            <span className="line-clamp-1">{prog.shortDescription ?? "—"}</span>
                          </TableCell>
                          {/* Per-category volgorde: verplaats deze activiteit een plek omhoog of
                              omlaag binnen deze sectie. De volgorde bepaalt hoe scholen het in het
                              portaal zien. */}
                          <TableCell className="whitespace-nowrap text-center">
                            <div className="inline-flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => reorderWithin(sub.items, idx, -1)}
                                disabled={idx === 0 || reordering || prog.offeringId == null}
                                aria-label="Naar boven"
                                title="Naar boven"
                                className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ChevronUpIcon className="size-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => reorderWithin(sub.items, idx, 1)}
                                disabled={idx === sub.items.length - 1 || reordering || prog.offeringId == null}
                                aria-label="Naar beneden"
                                title="Naar beneden"
                                className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ChevronDownIcon className="size-4" />
                              </button>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingId(String(prog.id))}
                            >
                              <PencilIcon className="size-3.5" /> Bewerken
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </section>
          ))}
        </div>
      )}

      <ActivityEditDialog
        programmeId={editingId}
        onClose={() => setEditingId(null)}
        onSaved={load}
      />
    </AdminShell>
  );
}
